import { ReactNode, createContext, useMemo } from 'react'
import dayjs, { Dayjs } from 'dayjs'
import { CalendarViewType } from '../enums/view-type'
import { NavigateOptions, useLocation, useNavigate } from 'react-router'
import { getDaysOfWeek } from '../hooks/use-week'
import { useDate, UseDateReturn } from '../hooks/use-date'
import { useStoredState } from '$hooks'

export type CalendarContext = {
  date: Dayjs,
  viewType: CalendarViewType,
  setDate: UseDateReturn['setDate'],
  navigate: (path: string, options?: NavigateOptions) => void,
  getWeekRange: () => { start: string, finish: string },
  showWorkingDaysOnly: boolean,
  setShowWorkingDaysOnly: (value: boolean) => void,
}

export const CalendarContext = createContext<CalendarContext>({
  date: dayjs.utc(),
  viewType: CalendarViewType.WEEK,
  setDate: () => {},
  navigate: () => {},
  getWeekRange: () => ({ start: '', finish: '' }),
  showWorkingDaysOnly: false,
  setShowWorkingDaysOnly: () => {},
})

export type CalendarProviderProps = {
  children: ReactNode
}

export function CalendarProvider({ children }: CalendarProviderProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const { date, setDate } = useDate()
  const [showWorkingDaysOnly, setShowWorkingDaysOnly] = useStoredState('calendar-working-days-filter', false)

  const viewType = useMemo(() => {
    const pattern = `${Object.values(CalendarViewType).join('|')}`
    const match = location.pathname.match(new RegExp(pattern))
    return match ? match[0] as CalendarViewType : CalendarViewType.WEEK
  }, [location.pathname])

  const navigateWithParams = (path: string, options?: NavigateOptions) => {
    navigate(path + location.search, options)
  }

  const getWeekRange = () => {
    const weekDays = getDaysOfWeek(date)
    const start = weekDays[0].startOf('day').add(date.utcOffset(), 'minute').toISOString()
    const finish = weekDays[weekDays.length - 1].endOf('day').add(date.utcOffset(), 'minute').toISOString()

    return { start, finish }
  }

  const context = {
    date: date,
    viewType,
    setDate,
    navigate: navigateWithParams,
    getWeekRange,
    showWorkingDaysOnly,
    setShowWorkingDaysOnly,
  }

  return (
    <CalendarContext.Provider value={context}>
      { children }
    </CalendarContext.Provider>
  )
}
